import { Typography, styled } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { computeDirectionLabels } from '~/utils/orientation';

const BaseViewportOrientation = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    ...theme.mixins.glassOverlay,
    padding: '1px 4px',
    borderRadius: 2,
    position: 'absolute',
    userSelect: 'none',
    zIndex: 3,
    fontFamily: "'Roboto Mono'",
    fontSize: 11,
}));

const ViewportOrientationTop = styled(BaseViewportOrientation)`
    top: 4px;
    left: calc(50% + 16px);
`;

const ViewportOrientationBottom = styled(BaseViewportOrientation)`
    bottom: 4px;
    left: calc(50% + 16px);
`;

const ViewportOrientationLeft = styled(BaseViewportOrientation)`
    top: calc(50% + 16px);
    left: 4px;
`;

const ViewportOrientationRight = styled(BaseViewportOrientation)`
    top: calc(50% + 16px);
    right: 4px;
`;

/**
 * Each edge component paired with its slot in the [left, top, right, bottom]
 * tuple that {@link computeDirectionLabels} returns.
 */
const EDGE_LABELS = [
    { edge: 'left', Component: ViewportOrientationLeft, index: 0 },
    { edge: 'top', Component: ViewportOrientationTop, index: 1 },
    { edge: 'right', Component: ViewportOrientationRight, index: 2 },
    { edge: 'bottom', Component: ViewportOrientationBottom, index: 3 },
] as const;

interface ViewportDirectionLabelsProp {
    viewportOrientation: Orientations;
}

const LabelDirections = ({
    viewportOrientation,
}: ViewportDirectionLabelsProp) => {
    const primaryImage = useRecoilValue(primaryImageState);
    if (!primaryImage) {
        return null;
    }

    const viewOrientation = PlaneView.computeViewOrientation(
        primaryImage.grid,
        viewportOrientation
    );
    const directionLabels = computeDirectionLabels(viewOrientation);

    return (
        <>
            {EDGE_LABELS.map(({ edge, Component, index }) => {
                const label = directionLabels[index];
                return (
                    <Component key={edge} sx={{ color: 'primary.main' }}>
                        {label}
                    </Component>
                );
            })}
        </>
    );
};

export default LabelDirections;
